import React, { useState } from 'react';
import { MainLayout } from '../../components/layout/MainLayout';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { stockService } from '../../api/services/stockService';
import { Stock, StockInput } from '@shared/types';
import { toast } from 'react-hot-toast';

type SortField = 'PartNumber' | 'BinNumber' | 'CreateDate';
type SortDirection = 'asc' | 'desc';

export default function StockManagement() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortField, setSortField] = useState<SortField>('PartNumber');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [formData, setFormData] = useState<StockInput>({
    PartNumber: '',
    Type: '',
    BinNumber: 0,
  });
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [editingStock, setEditingStock] = useState<Stock | null>(null);
  const [editFormData, setEditFormData] = useState<StockInput>({
    PartNumber: '',
    Type: '',
    BinNumber: 0,
  });

  // Fetch all stocks
  const { data: stocks, isLoading } = useQuery({
    queryKey: ['stocks'],
    queryFn: () => stockService.getStocks({ showOthers: true }),
  });

  // Add stock mutation
  const addStockMutation = useMutation({
    mutationFn: (stock: StockInput) => stockService.addStock(stock),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stocks'] });
      toast.success('Stock item added successfully');
      setFormData({
        PartNumber: '',
        Type: '',
        BinNumber: 0,
      });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to add stock item');
    },
  });

  // Update stock mutation
  const updateStockMutation = useMutation({
    mutationFn: ({ id, stock }: { id: number; stock: StockInput }) =>
      stockService.updateStock(id, stock),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stocks'] });
      toast.success('Stock item updated successfully');
      setEditModalOpen(false);
      setEditingStock(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update stock item');
    },
  });

  // Delete stock mutation
  const deleteStockMutation = useMutation({
    mutationFn: (id: number) => stockService.deleteStock(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stocks'] });
      toast.success('Stock item deleted successfully');
    },
    onError: () => {
      toast.error('Failed to delete stock item');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.PartNumber || formData.PartNumber.trim() === '') {
      toast.error('Please enter a Part Number');
      return;
    }

    // Validate PartNumber format
    const partNumberPattern = /\w+-\w+/;
    if (!partNumberPattern.test(formData.PartNumber)) {
      toast.error('Part Number must be in format XXX-XXX');
      return;
    }

    if (!formData.Type || formData.Type.trim() === '') {
      toast.error('Please enter a Type');
      return;
    }

    if (!formData.BinNumber || formData.BinNumber <= 0) {
      toast.error('Please enter a valid Bin Number');
      return;
    }

    addStockMutation.mutate(formData);
  };

  const handleEdit = (stock: Stock) => {
    setEditingStock(stock);
    setEditFormData({
      PartNumber: stock.PartNumber,
      Type: stock.Type,
      BinNumber: stock.BinNumber,
    });
    setEditModalOpen(true);
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!editFormData.PartNumber || editFormData.PartNumber.trim() === '') {
      toast.error('Please enter a Part Number');
      return;
    }

    // Validate PartNumber format
    const partNumberPattern = /\w+-\w+/;
    if (!partNumberPattern.test(editFormData.PartNumber)) {
      toast.error('Part Number must be in format XXX-XXX');
      return;
    }

    if (!editFormData.Type || editFormData.Type.trim() === '') {
      toast.error('Please enter a Type');
      return;
    }

    if (!editFormData.BinNumber || editFormData.BinNumber <= 0) {
      toast.error('Please enter a valid Bin Number');
      return;
    }

    if (editingStock) {
      updateStockMutation.mutate({
        id: editingStock.Id,
        stock: editFormData,
      });
    }
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingStock(null);
  };

  const handleDelete = (stock: Stock) => {
    if (window.confirm(`Are you sure you want to delete stock item with ID ${stock.Id}?`)) {
      deleteStockMutation.mutate(stock.Id);
    }
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getFilteredAndSortedStocks = () => {
    if (!stocks) return [];

    // First filter by search term
    let filteredStocks = stocks;
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredStocks = stocks.filter(
        (stock) =>
          stock.PartNumber.toLowerCase().includes(searchLower) ||
          stock.Type.toLowerCase().includes(searchLower)
      );
    }

    // Then sort
    return [...filteredStocks].sort((a, b) => {
      let aValue: string | number | Date = a[sortField];
      let bValue: string | number | Date = b[sortField];

      // Handle date comparison
      if (sortField === 'CreateDate') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Handle number comparison
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return (
        <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  return (
    <MainLayout>
      <div className="px-4 py-6 sm:px-0">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Stock Station
            </h2>
          </div>

          <div className="p-6">
            {/* Add Stock Form */}
            <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Add Stock Item
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-0 sm:flex sm:gap-4 sm:items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Part Number (XXX-XXX)
                  </label>
                  <input
                    type="text"
                    value={formData.PartNumber}
                    onChange={(e) => setFormData({ ...formData, PartNumber: e.target.value })}
                    placeholder="e.g., ABC-123"
                    required
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Type
                  </label>
                  <select
                    value={formData.Type}
                    onChange={(e) => setFormData({ ...formData, Type: e.target.value })}
                    required
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                  >
                    <option value="">Select Type</option>
                    <option value="SEAT COVER">SEAT COVER</option>
                    <option value="DASH COVER">DASH COVER</option>
                    <option value="CARGO LINNER">CARGO LINNER</option>
                  </select>
                </div>

                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Bin Number
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.BinNumber || ''}
                    onChange={(e) => setFormData({ ...formData, BinNumber: parseInt(e.target.value) || 0 })}
                    placeholder="Bin Number"
                    required
                    className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={addStockMutation.isPending}
                    className="w-full sm:w-auto px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {addStockMutation.isPending ? 'Adding...' : 'Add Stock'}
                  </button>
                </div>
              </form>
            </div>

            {/* Search */}
            <div className="mb-6 flex items-end gap-4">
              <div className="max-w-md flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Search by Part Number or Type
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="block w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                />
              </div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap pb-2">
                Items in Stock: {getFilteredAndSortedStocks().length}
              </div>
            </div>

            {/* Stock Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      ID
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                      onClick={() => handleSort('PartNumber')}
                    >
                      <div className="flex items-center gap-1">
                        Part Number
                        <SortIcon field="PartNumber" />
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Type
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                      onClick={() => handleSort('BinNumber')}
                    >
                      <div className="flex items-center gap-1">
                        Bin Number
                        <SortIcon field="BinNumber" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 select-none"
                      onClick={() => handleSort('CreateDate')}
                    >
                      <div className="flex items-center gap-1">
                        Created Date
                        <SortIcon field="CreateDate" />
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {isLoading ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                        Loading...
                      </td>
                    </tr>
                  ) : !stocks || stocks.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                        No stock items found
                      </td>
                    </tr>
                  ) : (
                    getFilteredAndSortedStocks().map((stock) => (
                      <tr key={stock.Id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {stock.Id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {stock.PartNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {stock.Type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {stock.BinNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {formatDate(stock.CreateDate)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(stock)}
                              className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(stock)}
                              className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Edit Stock Item
            </h3>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Part Number (XXX-XXX)
                </label>
                <input
                  type="text"
                  value={editFormData.PartNumber}
                  onChange={(e) => setEditFormData({ ...editFormData, PartNumber: e.target.value })}
                  placeholder="e.g., ABC-123"
                  required
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Type
                </label>
                <select
                  value={editFormData.Type}
                  onChange={(e) => setEditFormData({ ...editFormData, Type: e.target.value })}
                  required
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="">Select Type</option>
                  <option value="SEAT COVER">SEAT COVER</option>
                  <option value="DASH COVER">DASH COVER</option>
                  <option value="CARGO LINNER">CARGO LINNER</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Bin Number
                </label>
                <input
                  type="number"
                  min="1"
                  value={editFormData.BinNumber || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, BinNumber: parseInt(e.target.value) || 0 })}
                  placeholder="Bin Number"
                  required
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  disabled={updateStockMutation.isPending}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {updateStockMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseEditModal}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
